import { useRef, useState, type RefObject } from "react";
import { useScrollToSection, useIntersectionObserver } from "./hooks";
import { MobileNavbar } from "./navbar/mobile";
import { DesktopNavbar } from "./navbar/desktop";
import { sections, LandingPageSection } from "./sections/sections";
import { Footer } from "@/components/footer";

/** Holds the shared state for the landing page sections,
 * refs to them, and the tab bar that navs to them (mobile widths).
 * Coordinates the state between the current section and the different nav bars.
 *
 * To add or control which sections are displayed, edit `sections.tsx` */
export default function LandingPage() {
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "hero");
  const [manuallySelectedSection, setManuallySelectedSection] = useState<string | null>(null);

  const sectionRefs = sections.reduce(
    (acc, section) => {
      acc[section.id] = useRef(null);
      return acc;
    },
    {} as Record<string, RefObject<HTMLDivElement | null>>,
  );

  const { isScrolling, handleTabClick } = useScrollToSection(
    sectionRefs,
    setActiveSection,
    setManuallySelectedSection,
  );

  useIntersectionObserver(
    sectionRefs,
    activeSection,
    isScrolling,
    manuallySelectedSection,
    setActiveSection,
    setManuallySelectedSection,
  );

  const mobileNavTabs = sections
    .filter((section) => section.mobileNavTab)
    .map((section) => ({
      id: section.id,
      label: section.mobileNavTab!.label,
      icon: section.mobileNavTab!.icon,
    }));

  return (
    <>
      <MobileNavbar
        tabs={mobileNavTabs}
        activeSection={activeSection}
        onTabClick={handleTabClick}
      />
      <DesktopNavbar tabs={mobileNavTabs} onTabClick={handleTabClick} />
      <div className="-mt-16 overflow-hidden bg-background text-foreground antialiased selection:bg-lime-700/30">
        {sections.map((section) => (
          <LandingPageSection
            key={section.id}
            id={section.id}
            content={section.content}
            ref={sectionRefs[section.id]}
          />
        ))}
      </div>
      <Footer />
    </>
  );
}
